type LowercaseValues<Values extends string[]> = Lowercase<Values[number]>[];

export function createFeatureFlagConfig<Key extends string, Values extends boolean, DefaultValueType>(
  key: Key,
  values: boolean,
  defaultValue?: DefaultValueType
): {
  queryParam: `feature.${Lowercase<Key>}`;
  supportedValues: boolean;
  defaultValue: DefaultValueType;
  type: 'config';
};

export function createFeatureFlagConfig<Key extends string, Values extends string[]>(
  key: Key,
  values: readonly [...Values],
  defaultValue?: Values[number] | undefined
): { queryParam: `feature.${Lowercase<Key>}`; supportedValues: readonly [...LowercaseValues<Values>]; defaultValue: Values[number]; type: 'config' };

export function createFeatureFlagConfig<Key extends string, Values extends unknown, DefaultValueType>(key: Lowercase<Key>, values: Values, defaultValue?: DefaultValueType) {
  return {
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: Array.isArray(values) ? values.map(val => val.toLowerCase()) : values,
    defaultValue,
    type: 'config'
  } as const;
}

export function createBooleanFeatureFlag<Key extends string>(key: Key, defaultValue?: true) {
  return {
    queryParam: `feature.${key}`.toLowerCase(),
    supportedValues: [true, false],
    defaultValue,
    type: 'boolean'
  } as { queryParam: `feature.${Lowercase<Key>}`; defaultValue: true | false; supportedValues: [true, false]; type: 'boolean' };
}

//---------------------------------------------
// Add new features + supported values
export const FEATURE_FLAG_VALUES = {
  layout: createFeatureFlagConfig('layout', ['aloha', 'brannan']),
  guestsitetarget: createFeatureFlagConfig('guestsitetarget', ['bliss', 'charm']),
  nossr: createBooleanFeatureFlag('nossr'),
  joyWeb: createFeatureFlagConfig('joy-web', true),
  pseudoLocalization: createFeatureFlagConfig('pseudoloc', ['true', 'all']),
  addCashGift: createBooleanFeatureFlag('addcashgift'),
  registryGiftTracking: createBooleanFeatureFlag('registryGiftTracking'),
  bookmarkletPopup: createBooleanFeatureFlag('bookmarkletPopup'),
  productPromoFeed: createBooleanFeatureFlag('productPromoFeed'),
  externalRegistry: createBooleanFeatureFlag('externalRegistry'),
  enableRegistryPasswordUnlock: createBooleanFeatureFlag('enableRegistryPasswordUnlock'),
  disableRegistryPhotoUpload: createBooleanFeatureFlag('disableregistryphotoupload'),
  enableSsr: createBooleanFeatureFlag('enablessr'),
  enableCurationHero: createBooleanFeatureFlag('enableCurationHero'),
  enableCurationHeroVideo: createBooleanFeatureFlag('enableCurationHeroVideo'),
  giftReservation: createFeatureFlagConfig('giftReservation', ['true', 'r3', 'r4']),
  compactBuildPage: createBooleanFeatureFlag('compactBuildPage'),
  enableProductColorSwatch: createBooleanFeatureFlag('enableProductColorSwatch'),
  enableCountryDropdown: createBooleanFeatureFlag('enableCountryDropdown'),
  tileColorSwatchInteraction: createFeatureFlagConfig('tileColorSwatchInteraction', ['hover', 'click', 'hover-n-click']),
  doRegistryOnboardingCardsReset: createBooleanFeatureFlag('doRegistryOnboardingCardsReset'),
  enableNewCurationGrid: createBooleanFeatureFlag('enableNewCurationGrid'),
  enableRegistryOnboardingSurveyDialog: createBooleanFeatureFlag('enableRegistryOnboardingSurveyDialog'),
  registryOnboardingSurvey: createFeatureFlagConfig('registryOnboardingSurvey', ['force', 'reset', 'force5']),
  enableFullstoryTracking: createBooleanFeatureFlag('enableFullstoryTracking'),
  enableRegistryTransfer: createBooleanFeatureFlag('enableRegistryTransfer', true),
  enableCreateWeddingProgressBar: createBooleanFeatureFlag('enableCreateWeddingProgressbar'),
  enableClearCartFeature: createBooleanFeatureFlag('enableClearCartFeature'),
  enableHaveYouPurchasedModal: createBooleanFeatureFlag('enableHaveYouPurchasedModal', true),
  enableHaveYouPurchasedModalExternalLinkClick: createBooleanFeatureFlag('enableHaveYouPurchasedModalExternalLinkClick', true),
  enableCashFundCheckoutV2: createBooleanFeatureFlag('enableCashFundCheckoutV2', true),
  enableLoadingStyleApplicator: createBooleanFeatureFlag('enableLoadingStyleApplicator'),
  enableAddressVerificationModal: createBooleanFeatureFlag('enableAddressVerificationModal'),
  enableQuickAddLandingPage: createBooleanFeatureFlag('enableQuickAddLandingPage'),
  enableAddEditGiftNote: createBooleanFeatureFlag('enableAddEditGiftNote', true),
  enableOnboardingSurveyV2: createBooleanFeatureFlag('enableOnboardingSurveyV2', true),
  enableOnboardingExperimentBypass: createBooleanFeatureFlag('enableOnboardingExperimentBypass'),
  enableCurationHomeExpert: createBooleanFeatureFlag('enableCurationHomeExpert', true),
  enableRegistryTransferV2: createBooleanFeatureFlag('enableRegistryTransferV2', true),
  enableBuyForMyselfFromPurchaseConfirmation: createBooleanFeatureFlag('enableBuyForMyselfFromPurchaseConfirmation', true),
  enableProductsPrioritization: createBooleanFeatureFlag('enableProductsPrioritization', true),
  enableRandomLabelsGuestRegistryProducts: createBooleanFeatureFlag('enableRandomLabelsGuestRegistryProducts'),
  enableSplitPurchaseDialog: createBooleanFeatureFlag('enableSplitPurchaseDialog', true),
  enableShippingAddressWarning: createBooleanFeatureFlag('enableShippingAddressWarning', true),
  orderTrackingFeature: createBooleanFeatureFlag('orderTrackingFeature', true),
  enableDashboardV2: createBooleanFeatureFlag('enableDashboardV2', true),
  enableProductIndicators: createBooleanFeatureFlag('enableProductIndicators'),
  enableRegistryPhotoDirectUpload: createBooleanFeatureFlag('enableRegistryPhotoDirectUpload'),
  enablePostTransferBanner: createBooleanFeatureFlag('enablePostTransferBanner', true),
  enableMFASettings: createBooleanFeatureFlag('enableMFASettings', true),
  enableGuestRegistryTheme: createBooleanFeatureFlag('enableGuestRegistryTheme', true),
  enableRegistryCompletionDiscount: createBooleanFeatureFlag('enableRegistryCompletionDiscount'),
  enableHonourIntentSurvey: createBooleanFeatureFlag('enableHonourIntentSurvey', true),
  enableCurationsSection: createBooleanFeatureFlag('enableCurationsSection', true),
  enableRegistryRecommendation: createBooleanFeatureFlag('enableRegistryRecommendation', true),
  enableAffiliateCheckoutV2: createBooleanFeatureFlag('enableAffiliateCheckoutV2', true),
  shoppingCartV2: createBooleanFeatureFlag('shoppingCartV2'),
  enableNewExternalRegistry: createBooleanFeatureFlag('enableNewExternalRegistry'),
  enableCategoryShowcase: createBooleanFeatureFlag('enableCategoryShowcase', true),
  enableRegistrySettingsDialog: createBooleanFeatureFlag('enableRegistrySettingsDialog', true),
  enableGroupGifting: createBooleanFeatureFlag('enableGroupGifting', true),
  enableGalleryLightboxZooming: createBooleanFeatureFlag('enableGalleryLightboxZooming'),
  enableChecklistInGiftBasketWidget: createBooleanFeatureFlag('enableChecklistInGiftBasketWidget'),

  /**
   * Is being AB tested
   */
  enableShopNavInManage: createBooleanFeatureFlag('enableShopNavInManage'),
  enableRegistryVisibilityDialog: createBooleanFeatureFlag('enableRegistryVisibilityDialog', true),
  enableNewRegistryOverview: createBooleanFeatureFlag('enableNewRegistryOverview'),
  enableGalleryLightbox: createBooleanFeatureFlag('enableGalleryLightbox'),
  /**
   * Prevent using cached SSR html.
   */
  skipSsrCache: createBooleanFeatureFlag('skipssrcache'),
  logSsrUserAgent: createBooleanFeatureFlag('logSsrUserAgent')
};

export type FeatureFlags = typeof FEATURE_FLAG_VALUES;
